import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChatCircleDots, X, PaperPlaneTilt } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hello. I'm your estate planning guide. Ask me about wills, trusts, power of attorney or probate." },
  ]);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: text, session_id: session });
      setSession(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "I'm unavailable right now. Please try again shortly." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-toggle"
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-[60] w-16 h-16 rounded-full bg-gold text-ink flex items-center justify-center shadow-2xl"
      >
        {open ? <X size={26} weight="bold" /> : <ChatCircleDots size={28} weight="fill" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-window"
            className="fixed bottom-28 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] h-[540px] bg-[#0e0e0e] border border-border rounded-lg flex flex-col overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-border bg-ink">
              <div className="serif text-xl text-bone">Estate Planning Assistant</div>
              <div className="text-bone/40 text-xs mt-0.5">Ask anything — general guidance</div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rounded-lg ${m.role === "user" ? "bg-gold text-ink" : "bg-muted text-bone/90 border border-border"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && <div className="flex justify-start"><div className="bg-muted border border-border px-4 py-3 rounded-lg text-gold text-sm">Thinking…</div></div>}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-border flex gap-2">
              <input
                data-testid="chat-input"
                value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type your question…"
                className="flex-1 bg-ink border border-border px-3 py-2.5 text-sm text-bone outline-none focus:border-gold transition-colors duration-300"
              />
              <button data-testid="chat-send" onClick={send} className="w-11 flex items-center justify-center bg-gold text-ink hover:bg-bone transition-colors duration-300">
                <PaperPlaneTilt size={20} weight="fill" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
